import classNames from 'classnames';
import { useIntl } from 'react-intl';
import ConditionAlert from '@alltrails/conditions/components/ConditionAlert';
import TemperatureRange from '@alltrails/conditions/components/TemperatureRange';
import ShimmerContainer from '@alltrails/core/components/ShimmerContainer';
import WeatherIcon from '@alltrails/denali/components/WeatherIcon';
import Sunrise from '@alltrails/denali/icons/Sunrise';
import Sunset from '@alltrails/denali/icons/Sunset';
import Weather12 from '@alltrails/denali/icons/Weather12';
import { Day, SelectedDay } from './dayTypes';
import styles from './DesktopDailySummaries.module.scss';

type DesktopDailySummariesProps = {
  days: (Day | SelectedDay)[];
  onChange: (utcMs: number, index: number) => void;
  selectedUTCMs: number;
};

const DesktopDailySummaries = ({ days, onChange, selectedUTCMs }: DesktopDailySummariesProps) => {
  const intl = useIntl();

  const formatTemp = (temp: number) => intl.formatNumber(temp, { style: 'unit', unit: 'degree', unitDisplay: 'narrow', maximumFractionDigits: 0 });

  const renderSelectedContent = ({ alert, precipitationChance, sunrise, sunset, summary, tempCurrent, tempHigh, tempLow }: SelectedDay) => {
    return (
      <div className={styles.selectedContent}>
        <div className={styles.nonAlertContent}>
          <div>
            <div className={styles.selectedTemps}>
              {tempCurrent !== undefined ? (
                <div className={styles.selectedTemp}>{formatTemp(tempCurrent)}</div>
              ) : (
                <>
                  <div className={styles.selectedTemp}>{formatTemp(tempHigh)}</div>
                  <div className={styles.selectedTemp}>{formatTemp(tempLow)}</div>
                </>
              )}
            </div>
            <div className={styles.summaryText}>{summary}</div>
          </div>
          <div className={styles.selectedStats}>
            {sunrise ? (
              <div className={styles.stat}>
                <Sunrise color="--color-text-secondary" size="xs" />
                {intl.formatTime(sunrise, { timeZone: 'UTC' })}
              </div>
            ) : null}
            {sunset ? (
              <div className={styles.stat}>
                <Sunset color="--color-text-secondary" size="xs" />
                {intl.formatTime(sunset, { timeZone: 'UTC' })}
              </div>
            ) : null}
            {precipitationChance !== undefined ? (
              <div className={styles.stat}>
                <Weather12 color="--color-text-secondary" size="xs" />
                {intl.formatNumber(Math.round(precipitationChance), { style: 'unit', unit: 'percent' })}
              </div>
            ) : null}
          </div>
        </div>
        {alert && <ConditionAlert {...alert} />}
      </div>
    );
  };

  const renderOpenContent = (day: Day | SelectedDay) =>
    'summary' in day ? renderSelectedContent(day) : <SelectedContentLoading renderShimmerContainer />;

  const { maxTemp, minTemp } = days.reduce(
    (current, { tempHigh, tempLow }) => ({
      maxTemp: Math.max(current.maxTemp, tempHigh),
      minTemp: Math.min(current.minTemp, tempLow)
    }),
    { maxTemp: -1 * Infinity, minTemp: Infinity }
  );

  return (
    <div className={styles.container}>
      {days.map((day, index) => {
        const { gradient, tempHigh, tempLow, utcMs, weatherIconCode } = day;
        const isSelected = selectedUTCMs === utcMs;
        const weekday = intl.formatDate(new Date(utcMs), { weekday: 'long', timeZone: 'UTC' });

        const temperatureElement = gradient ? (
          <TemperatureRange
            currentMax={tempHigh}
            currentMin={tempLow}
            currentTemp={isSelected && 'tempCurrent' in day ? day.tempCurrent : undefined}
            gradientStops={gradient}
            max={maxTemp}
            min={minTemp}
          />
        ) : (
          <div className={styles.summaryTemps}>
            <span>{formatTemp(tempLow)}</span>
            <span>/</span>
            {formatTemp(tempHigh)}
          </div>
        );

        return (
          <button
            key={utcMs}
            aria-label={weekday}
            className={styles.dailySummaryButton}
            onClick={!isSelected ? () => onChange(utcMs, index) : undefined}
          >
            <div className={styles.dailySummary}>
              <div className={styles.iconAndDay}>
                <WeatherIcon colored iconCode={weatherIconCode} />
                {weekday}
              </div>
              {temperatureElement}
            </div>
            {isSelected ? renderOpenContent(day) : null}
          </button>
        );
      })}
    </div>
  );
};

export default DesktopDailySummaries;

const SelectedContentLoading = ({ renderShimmerContainer }: { renderShimmerContainer: boolean }) => {
  const contents = (
    <div className={styles.nonAlertContent}>
      <div>
        <div className={styles.selectedTemps}>
          <div className={classNames(styles.selectedTemp, styles.loading)}>&nbsp;</div>
        </div>
        <div className={classNames(styles.summaryText, styles.loading)}>&nbsp;</div>
      </div>
      <div className={styles.selectedStats}>
        <div className={classNames(styles.stat, styles.loading)}>&nbsp;</div>
        <div className={classNames(styles.stat, styles.loading)}>&nbsp;</div>
        <div className={classNames(styles.stat, styles.loading)}>&nbsp;</div>
      </div>
    </div>
  );

  return renderShimmerContainer ? (
    <ShimmerContainer className={styles.selectedContent}>{contents}</ShimmerContainer>
  ) : (
    <div className={styles.selectedContent}>{contents}</div>
  );
};

export const DesktopDailySummariesLoading = () => {
  return (
    <ShimmerContainer>
      <div className={styles.container}>
        {[...Array(7)].map((_, i) => (
          <div key={`daily-summary-${i}`} className={styles.dailySummaryButton}>
            <div className={styles.dailySummary}>
              <div className={classNames(styles.iconAndDay, styles.loading)}>&nbsp;</div>
              <div className={classNames(styles.summaryTemps, styles.loading)}>&nbsp;</div>
            </div>
            {i === 0 ? <SelectedContentLoading renderShimmerContainer={false} /> : null}
          </div>
        ))}
      </div>
    </ShimmerContainer>
  );
};
